import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API } from '@/config/api';

// Reads the Notion-synced mirror (notion_projects), enriched server-side with
// next_action/is_overdue/is_blocked. Notion itself is never written to from here --
// updateFase only writes to dondoit's own fase_operativa column.
export const useOperationsProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/operations/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching operations projects:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/operations/sync-status`);
      setSyncStatus(response.data);
    } catch (err) {
      console.error('Error fetching sync status:', err);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchSyncStatus();
  }, [fetchProjects, fetchSyncStatus]);

  const syncNow = async (userId) => {
    try {
      setSyncing(true);
      const headers = process.env.REACT_APP_NOTION_SYNC_SECRET
        ? { 'x-notion-sync-secret': process.env.REACT_APP_NOTION_SYNC_SECRET }
        : {};
      const response = await axios.post(`${API}/notion/sync`, null, { params: { user_id: userId }, headers });
      await fetchProjects();
      await fetchSyncStatus();
      return response.data;
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || err.message };
    } finally {
      setSyncing(false);
    }
  };

  const updateFase = async (projectId, faseOperativa, updatedByUserId, faseResponsibleUserId) => {
    try {
      await axios.patch(`${API}/operations/projects/${projectId}/fase`, {
        fase_operativa: faseOperativa,
        updated_by_user_id: updatedByUserId,
        fase_responsible_user_id: faseResponsibleUserId || null,
      });
      await fetchProjects();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  };

  const setBlocked = async (projectId, reason, updatedByUserId) => {
    try {
      await axios.patch(`${API}/operations/projects/${projectId}/blocked`, {
        blocked_manual_reason: reason || null,
        updated_by_user_id: updatedByUserId,
      });
      await fetchProjects();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  };

  const updateProcess = async (projectId, { process_type, process_step, notify }, updatedByUserId) => {
    try {
      const r = await axios.patch(`${API}/operations/projects/${projectId}/process`, {
        process_type: process_type ?? null,
        process_step: process_step ?? null,
        notify: !!notify,
        updated_by_user_id: updatedByUserId,
      });
      await fetchProjects();
      return { success: true, notified: r.data?.notified || [] };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  };

  const archiveProject = async (projectId, archived, updatedByUserId) => {
    try {
      await axios.patch(`${API}/operations/projects/${projectId}/archive`, {
        archived,
        updated_by_user_id: updatedByUserId,
      });
      await fetchProjects();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || err.message };
    }
  };

  return { projects, loading, error, syncing, syncStatus, refetch: fetchProjects, syncNow, updateFase, updateProcess, setBlocked, archiveProject };
};
